import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SearchableCombobox } from '../components/ui/searchable-combobox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Search, Loader2, RefreshCw, Package, Plus, _Save,
  ChevronRight, ChevronDown, Boxes, GitBranch, Tag, Printer, FileSpreadsheet,
  Pencil, Trash2, Lock, AlertTriangle, Download, Mail, Send, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, printTable } from '../lib/printUtils';

export default function MaestroMateriales() {
  const { user } = useAuth();
  const isAdminGer = user?.rol === 'admin' || user?.rol === 'gerencia';
  const [activeTab, setActiveTab] = useState('arbol');
  const [loading, setLoading] = useState(false);

  // Arbol
  const [arbol, setArbol] = useState(null);
  const [expanded, setExpanded] = useState({});

  // Clasificar
  const [subfamilias, setSubfamilias] = useState([]);
  const [expandedFams, setExpandedFams] = useState({});
  const [famMaterials, setFamMaterials] = useState({});
  const [famLoading, setFamLoading] = useState({});
  const [searchMat, setSearchMat] = useState('');
  const [matResults, setMatResults] = useState([]);
  const [matLoading, setMatLoading] = useState(false);
  const [classifying, setClassifying] = useState(null);

  // Create
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ descripcion: '', descripcion2: '', medida: '', unidad: 'UND', codpf: '', marca: '' });
  const [saving, setSaving] = useState(false);

  // Listado
  const [materiales, setMateriales] = useState([]);
  const [totalMat, setTotalMat] = useState(0);
  const [searchList, setSearchList] = useState('');
  const [categoria, setCategoria] = useState('');

  // Iter 236.x (Renzo, feb-2026) — Editar/Eliminar material sin movimientos
  //  * `movsByCodigo`: map código → {puede_editar_nombre, puede_eliminar, movimientos}
  //  * `editModal`: dialog para renombrar descripción/descripcion2/medida
  //  * `deleteModal`: dialog de confirmación de eliminación
  const [movsByCodigo, setMovsByCodigo] = useState({});
  const [editModal, setEditModal] = useState({ open: false, material: null });
  const [editForm, setEditForm] = useState({ descripcion: '', descripcion2: '', medida: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, material: null, movs: null });
  const [deleteSaving, setDeleteSaving] = useState(false);

  // Iter 414 (Renzo) — Listado del Maestro por correo (solo Admin/Gerencia):
  // descarga del Excel (artículos vigentes + hoja "Sin clasificar") y envío por
  // correo a Gerencia (simulado en pruebas, real en producción).
  const [listadoBusy, setListadoBusy] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState(['rmartinez@chirinosteel.com']);
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const descargarListadoExcel = useCallback(async () => {
    setListadoBusy(true);
    try {
      const res = await api.get('/bodega/maestro-materiales/listado-excel', { responseType: 'blob', timeout: 60000 });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      const hoy = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `Maestro_Materiales_${hoy}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success('Listado descargado');
    } catch (err) {
      toast.error('No se pudo descargar el listado', { description: err.response?.data?.detail });
    } finally {
      setListadoBusy(false);
    }
  }, []);

  const addEmailRecipient = () => {
    const e = emailInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error('Correo inválido'); return; }
    if (emailTo.includes(e)) { toast.info('Ese correo ya está en la lista'); setEmailInput(''); return; }
    setEmailTo((prev) => [...prev, e]);
    setEmailInput('');
  };

  const removeEmailRecipient = (e) => setEmailTo((prev) => prev.filter((x) => x !== e));

  const enviarListadoCorreo = async () => {
    if (emailTo.length === 0) { toast.error('Agrega al menos un destinatario'); return; }
    setEmailSending(true);
    try {
      const res = await api.post('/bodega/maestro-materiales/listado-excel/enviar', { to: emailTo });
      const r = res.data?.resumen || {};
      if (res.data?.simulado) {
        toast.success('Envío simulado (ambiente de pruebas)', {
          description: `${r.total ?? 0} artículos · ${r.sin_clasificar ?? 0} sin clasificar · a ${emailTo.join(', ')}`,
        });
      } else {
        toast.success('Listado enviado por correo', {
          description: `${r.total ?? 0} artículos · a ${emailTo.join(', ')}`,
        });
      }
      setEmailModal(false);
    } catch (err) {
      toast.error('No se pudo enviar el listado', { description: err.response?.data?.detail });
    } finally {
      setEmailSending(false);
    }
  };

  const loadArbol = useCallback(async () => {
    setLoading(true);
    try {
      const [arbolRes, sfRes] = await Promise.all([
        api.get('/bodega/arbol-clasificacion'),
        api.get('/bodega/subfamilias-lista'),
      ]);
      setArbol(arbolRes.data);
      setSubfamilias(sfRes.data.subfamilias || []);
    } catch { toast.error('Error cargando clasificación'); }
    finally { setLoading(false); }
  }, []);

  const loadMateriales = useCallback(async () => {
    setMatLoading(true);
    try {
      const p = new URLSearchParams({ limit: '150' });
      if (searchList) p.set('q', searchList);
      if (categoria) p.set('categoria', categoria);
      const res = await api.get(`/bodega/maestro-materiales?${p}`);
      const items = res.data.materiales || [];
      setMateriales(items);
      setTotalMat(res.data.total || 0);
      // Iter 236.x — batch check movimientos para habilitar botones editar/eliminar
      const codigos = items.map((m) => m.codigo).filter(Boolean);
      if (codigos.length > 0) {
        try {
          const r2 = await api.post('/bodega/maestro-materiales/verificar-movimientos', { codigos });
          setMovsByCodigo(r2.data?.resultados || {});
        } catch {
          setMovsByCodigo({});
        }
      } else {
        setMovsByCodigo({});
      }
    } catch { /* empty */ }
    finally { setMatLoading(false); }
  }, [searchList, categoria]);

  useEffect(() => { loadArbol(); }, [loadArbol]);
  useEffect(() => { if (activeTab === 'listado') loadMateriales(); }, [activeTab, loadMateriales]);

  // Carlos (10/05/2026): imprimir y exportar Maestro Materiales
  const imprimirMateriales = () => {
    if (!materiales.length) { toast.warning('Sin materiales para imprimir'); return; }
    printTable({
      title: 'Maestro de Materiales',
      subtitle: `${materiales.length} materiales · Búsqueda: ${searchList || 'todas'} · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total en sistema', value: totalMat },
        { label: 'En archivo', value: materiales.length },
        { label: 'Búsqueda', value: searchList || '-' },
      ],
      columns: [
        { key: 'descripcion', label: 'Descripción' },
        { key: 'medida', label: 'Medida' },
        { key: 'unidad', label: 'Und', align: 'center' },
        { key: 'concepto', label: 'Concepto' },
        { key: 'familia', label: 'Familia' },
        { key: 'subfamilia', label: 'Subfamilia' },
        { key: 'codpf', label: 'CodPF', mono: true, align: 'center' },
      ],
      rows: materiales,
    });
  };

  const exportarMaterialesExcel = () => {
    if (!materiales.length) { toast.warning('Sin materiales para exportar'); return; }
    exportToCSV({
      filename: 'maestro_materiales',
      title: 'CHIRINO STEEL · Maestro de Materiales',
      summary: [
        { label: 'Total en sistema', value: totalMat },
        { label: 'En archivo', value: materiales.length },
        { label: 'Búsqueda', value: searchList || '-' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'descripcion', label: 'Descripción' },
        { key: 'descripcion2', label: 'Descripción 2' },
        { key: 'medida', label: 'Medida' },
        { key: 'unidad', label: 'Unidad' },
        { key: 'concepto', label: 'Concepto' },
        { key: 'familia', label: 'Familia' },
        { key: 'subfamilia', label: 'Subfamilia' },
        { key: 'codpf', label: 'Cód. PF' },
        { key: 'marca', label: 'Marca' },
      ],
      rows: materiales,
    });
  };

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // Clasificar: load materials for a subfamilia
  const toggleFamExpand = async (codpf) => {
    if (expandedFams[codpf]) {
      setExpandedFams(prev => ({ ...prev, [codpf]: false }));
      return;
    }
    setExpandedFams(prev => ({ ...prev, [codpf]: true }));
    setFamLoading(prev => ({ ...prev, [codpf]: true }));
    try {
      const res = await api.get(`/bodega/materiales-por-subfamilia/${codpf}`);
      setFamMaterials(prev => ({ ...prev, [codpf]: res.data.materiales || [] }));
    } catch { /* empty */ }
    finally { setFamLoading(prev => ({ ...prev, [codpf]: false })); }
  };

  const searchMats = async () => {
    if (!searchMat.trim()) return;
    setMatLoading(true);
    try {
      const res = await api.get(`/bodega/maestro-materiales?q=${encodeURIComponent(searchMat)}&limit=50`);
      setMatResults(res.data.materiales || []);
    } catch { /* empty */ }
    finally { setMatLoading(false); }
  };

  const handleClasificar = async (codigo, codpf) => {
    setClassifying(codigo);
    try {
      const res = await api.put(`/bodega/clasificar-material/${codigo}?codpf=${codpf}`);
      toast.success(`Clasificado → ${res.data.familia} / ${res.data.subfamilia}`);
      setMatResults(prev => prev.map(m => m.codigo === codigo ? {
        ...m, concepto: res.data.concepto, familia_nombre: res.data.familia,
        subfamilia_nombre: res.data.subfamilia, cuenta_gasto: res.data.cta_gasto, codpf,
      } : m));
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setClassifying(null); }
  };

  const handleCreate = async () => {
    if (!createForm.descripcion) { toast.error('Ingrese descripción'); return; }
    if (!createForm.codpf) { toast.error('Seleccione subfamilia'); return; }
    setSaving(true);
    try {
      const res = await api.post('/bodega/maestro-materiales', createForm);
      toast.success(res.data.message);
      setCreateModal(false);
      setCreateForm({ descripcion: '', descripcion2: '', medida: '', unidad: 'UND', codpf: '', marca: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // Iter 236.x (Renzo, feb-2026) — Editar nombre / Eliminar sólo sin movimientos
  const openEditMaterial = (m) => {
    setEditForm({
      descripcion: m.descripcion || '',
      descripcion2: m.descripcion2 || '',
      medida: m.medida || '',
    });
    setEditModal({ open: true, material: m });
  };

  const saveEditMaterial = async () => {
    const m = editModal.material;
    if (!m?.codigo) return;
    if (!editForm.descripcion.trim()) { toast.error('La descripción es obligatoria'); return; }
    setEditSaving(true);
    try {
      await api.put(`/bodega/maestro-materiales/${encodeURIComponent(m.codigo)}`, {
        descripcion: editForm.descripcion.trim(),
        descripcion2: editForm.descripcion2.trim(),
        medida: editForm.medida.trim(),
      });
      toast.success(`Material ${m.codigo} actualizado`);
      setEditModal({ open: false, material: null });
      loadMateriales();
    } catch (err) {
      toast.error('No se pudo modificar', { description: err.response?.data?.detail });
    } finally {
      setEditSaving(false);
    }
  };

  const openDeleteMaterial = async (m) => {
    // Refresca movimientos on-demand para tener el detalle actualizado
    try {
      const r = await api.get(`/bodega/maestro-materiales/${encodeURIComponent(m.codigo)}/puede-modificar`);
      setDeleteModal({ open: true, material: m, movs: r.data });
    } catch (err) {
      toast.error('No se pudo verificar movimientos', { description: err.response?.data?.detail });
    }
  };

  const confirmDeleteMaterial = async () => {
    const m = deleteModal.material;
    if (!m?.codigo) return;
    setDeleteSaving(true);
    try {
      await api.delete(`/bodega/maestro-materiales/${encodeURIComponent(m.codigo)}`);
      toast.success(`Material ${m.codigo} eliminado`);
      setDeleteModal({ open: false, material: null, movs: null });
      loadMateriales();
    } catch (err) {
      toast.error('No se pudo eliminar', { description: err.response?.data?.detail });
    } finally {
      setDeleteSaving(false);
    }
  };

  // Group subfamilias by concepto -> familia for the Clasificar view
  const grouped = {};
  for (const sf of subfamilias) {
    if (!grouped[sf.concepto]) grouped[sf.concepto] = {};
    if (!grouped[sf.concepto][sf.familia]) grouped[sf.concepto][sf.familia] = [];
    grouped[sf.concepto][sf.familia].push(sf);
  }

  return (
    <div className="space-y-5" data-testid="maestro-materiales-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Boxes className="w-6 h-6 text-violet-600" /> Maestro de Materiales
          </h1>
          <p className="text-sm text-muted-foreground">Clasificación, edición y gestión de materiales.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdminGer && (
            <>
              <Button
                variant="outline"
                onClick={descargarListadoExcel}
                disabled={listadoBusy}
                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                title="Descargar el listado de artículos vigentes en Excel (incluye hoja 'Sin clasificar')"
                data-testid="btn-descargar-listado-excel"
              >
                {listadoBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Descargar Excel
              </Button>
              <Button
                variant="outline"
                onClick={() => { setEmailInput(''); setEmailTo(['rmartinez@chirinosteel.com']); setEmailModal(true); }}
                className="text-blue-700 border-blue-300 hover:bg-blue-50"
                title="Enviar el listado por correo a Gerencia"
                data-testid="btn-enviar-listado-correo"
              >
                <Mail className="w-4 h-4 mr-2" /> Enviar por correo
              </Button>
            </>
          )}
          <Button onClick={() => setCreateModal(true)} className="bg-violet-600 hover:bg-violet-700">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Material
          </Button>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { id: 'arbol', label: 'Árbol de Clasificación', icon: GitBranch },
          { id: 'clasificar', label: 'Clasificar Materiales', icon: Tag },
          { id: 'listado', label: 'Listado General', icon: Package },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === t.id ? 'bg-white shadow-sm text-violet-700' : 'text-gray-600'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ÁRBOL */}
      {activeTab === 'arbol' && (
        loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div> : arbol && (
          <div className="space-y-2">
            <div className="text-xs text-gray-500 mb-3">
              {arbol.total_classified} de {arbol.total_materials} materiales clasificados ({Math.round(arbol.total_classified / arbol.total_materials * 100)}%)
            </div>
            {arbol.arbol?.map(tipo => (
              <div key={tipo.tipo} className="border rounded-lg overflow-hidden">
                <button onClick={() => toggle(`t_${tipo.tipo}`)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 text-white hover:bg-gray-700 text-sm font-medium">
                  <span className="flex items-center gap-2">
                    {expanded[`t_${tipo.tipo}`] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    {tipo.nombre}
                  </span>
                  <span className="text-gray-400">{tipo.count} materiales</span>
                </button>
                {expanded[`t_${tipo.tipo}`] && tipo.conceptos?.map(conc => (
                  <div key={conc.nombre}>
                    <button onClick={() => toggle(`c_${conc.nombre}`)}
                      className="w-full flex items-center justify-between px-6 py-2 hover:bg-blue-50 border-t text-sm">
                      <span className="flex items-center gap-2">
                        {expanded[`c_${conc.nombre}`] ? <ChevronDown className="w-3.5 h-3.5 text-blue-600" /> : <ChevronRight className="w-3.5 h-3.5 text-blue-600" />}
                        <span className="font-medium text-blue-800">{conc.nombre}</span>
                      </span>
                      <span className="text-gray-400 text-xs">{conc.count}</span>
                    </button>
                    {expanded[`c_${conc.nombre}`] && conc.familias?.map(fam => (
                      <div key={fam.nombre}>
                        <button onClick={() => toggle(`f_${conc.nombre}_${fam.nombre}`)}
                          className="w-full flex items-center justify-between px-10 py-1.5 hover:bg-violet-50 border-t border-gray-100 text-xs">
                          <span className="flex items-center gap-2">
                            {expanded[`f_${conc.nombre}_${fam.nombre}`] ? <ChevronDown className="w-3 h-3 text-violet-600" /> : <ChevronRight className="w-3 h-3 text-violet-600" />}
                            <span className="font-medium text-violet-800">{fam.nombre}</span>
                          </span>
                          <span className="text-gray-400">{fam.count}</span>
                        </button>
                        {expanded[`f_${conc.nombre}_${fam.nombre}`] && fam.subfamilias?.map((sf, i) => (
                          <div key={i} className="flex items-center justify-between px-14 py-1 border-t border-gray-50 text-[11px] hover:bg-green-50">
                            <span className="text-gray-700">{sf.nombre}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-[9px] bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded font-mono">{sf.cta_gasto}</span>
                              <span className="text-[9px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-mono">{sf.cta_alegra}</span>
                              <span className="text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{sf.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )
      )}

      {/* CLASIFICAR */}
      {activeTab === 'clasificar' && (
        <div className="space-y-4">
          {/* Search bar */}
          <div className="bg-white border rounded-lg p-4 flex gap-3 items-end">
            <div className="flex-1 max-w-md">
              <label className="block text-xs font-medium mb-1">Buscar material</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Descripción o código..." value={searchMat}
                  onChange={(e) => setSearchMat(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchMats()} className="pl-9" />
              </div>
            </div>
            <Button onClick={searchMats} disabled={matLoading}>
              {matLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>

          {/* Search results */}
          {matResults.length > 0 && (
            <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
              <table className="w-full text-xs">
                <thead className="sticky top-14 lg:top-0 z-20">
                  <tr className="bg-gray-800 text-white">
                    <th className="px-3 py-2">Descripción</th>
                    <th className="px-3 py-2">Concepto</th>
                    <th className="px-3 py-2">Familia</th>
                    <th className="px-3 py-2">Subfamilia</th>
                    <th className="px-3 py-2">Cta.</th>
                    <th className="px-3 py-2 w-64">Cambiar Clasificación</th>
                  </tr>
                </thead>
                <tbody>
                  {matResults.map((m, i) => (
                    <tr key={i} className={`border-t hover:bg-violet-50/30 ${!m.subfamilia_nombre ? 'bg-amber-50/30' : ''}`}>
                      <td className="px-3 py-1.5 font-medium">{m.descripcion} {m.descripcion2 && <span className="text-gray-400">{m.descripcion2}</span>}</td>
                      <td className="px-3 py-1.5 text-[10px]">{m.concepto || <span className="text-red-400">Sin clasificar</span>}</td>
                      <td className="px-3 py-1.5 text-[10px] text-violet-700">{m.familia_nombre || '-'}</td>
                      <td className="px-3 py-1.5 text-[10px] text-green-700">{m.subfamilia_nombre || '-'}</td>
                      <td className="px-3 py-1.5 font-mono text-[9px]">{m.cuenta_gasto || '-'}</td>
                      <td className="px-3 py-1.5">
                        <select value={m.codpf || ''}
                          onChange={(e) => e.target.value && handleClasificar(m.codigo, e.target.value)}
                          disabled={classifying === m.codigo}
                          className="w-full h-7 px-2 rounded border border-input text-[10px]">
                          <option value="">-- Seleccionar --</option>
                          {subfamilias.map((sf, j) => (
                            <option key={j} value={sf.codpf}>{sf.concepto} / {sf.familia} / {sf.subfamilia}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Browse by family */}
          <h3 className="font-semibold text-sm text-gray-700 border-t pt-4">Navegar por Familia</h3>
          <div className="space-y-1">
            {Object.entries(grouped).map(([concepto, familias]) => (
              <div key={concepto} className="border rounded-lg overflow-hidden">
                <button onClick={() => toggle(`cls_${concepto}`)}
                  className="w-full flex items-center justify-between px-4 py-2 bg-blue-50 hover:bg-blue-100 text-sm">
                  <span className="flex items-center gap-2">
                    {expanded[`cls_${concepto}`] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    <span className="font-medium text-blue-800">{concepto}</span>
                  </span>
                </button>
                {expanded[`cls_${concepto}`] && Object.entries(familias).map(([familia, sfs]) => (
                  <div key={familia}>
                    {sfs.map((sf) => (
                      <div key={sf.codpf}>
                        <button onClick={() => toggleFamExpand(sf.codpf)}
                          className="w-full flex items-center justify-between px-8 py-1.5 hover:bg-violet-50 border-t border-gray-100 text-xs">
                          <span className="flex items-center gap-2">
                            {expandedFams[sf.codpf] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            <span className="text-violet-700">{sf.familia}</span>
                            <span className="text-gray-400">→</span>
                            <span className="text-green-700">{sf.subfamilia}</span>
                          </span>
                          <span className="text-[9px] font-mono text-gray-400">{sf.cta_gasto}</span>
                        </button>
                        {expandedFams[sf.codpf] && (
                          <div className="bg-gray-50 px-12 py-2">
                            {famLoading[sf.codpf] ? (
                              <Loader2 className="w-4 h-4 animate-spin text-violet-600" />
                            ) : (famMaterials[sf.codpf] || []).length === 0 ? (
                              <p className="text-gray-400 text-xs">No hay materiales en esta subfamilia</p>
                            ) : (
                              <div className="space-y-0.5">
                                {(famMaterials[sf.codpf] || []).map((m, mi) => (
                                  <div key={mi} className="flex items-center justify-between text-[11px] py-0.5">
                                    <span>{m.descripcion} {m.descripcion2 && <span className="text-gray-400">{m.descripcion2}</span>} {m.medida && <span className="text-gray-400">({m.medida})</span>}</span>
                                    <span className="text-gray-400">{m.unidad}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LISTADO */}
      {activeTab === 'listado' && (
        <>
          <div className="bg-white border rounded-lg p-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px] max-w-sm">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Descripción o código..." value={searchList}
                  onChange={(e) => setSearchList(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadMateriales()} className="pl-9" />
              </div>
            </div>
            <div className="flex gap-1">
              {[{ val: '', label: 'Todos' }, { val: 'herramienta', label: 'Herram.' }, { val: 'insumo', label: 'Insumos' }, { val: 'stock', label: 'Stock' }].map(c => (
                <button key={c.val} onClick={() => setCategoria(c.val)}
                  className={`px-3 py-1.5 text-xs rounded-md border ${categoria === c.val ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-300'}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <Button variant="outline" onClick={loadMateriales} disabled={matLoading}>
              <RefreshCw className={`w-4 h-4 ${matLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" onClick={imprimirMateriales} title="Imprimir listado" data-testid="btn-print-mat-listado">
              <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
            </Button>
            <Button variant="outline" size="sm" onClick={exportarMaterialesExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-mat-listado">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
            <span className="text-xs text-gray-500">{totalMat}</span>
          </div>

          <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
            <table className="w-full text-xs">
              <thead className="sticky top-14 lg:top-0 z-20">
                <tr className="bg-gray-800 text-white">
                  <th className="px-3 py-2">Descripción</th>
                  <th className="px-3 py-2">Medida</th>
                  <th className="px-3 py-2 text-center">Und</th>
                  <th className="px-3 py-2">Concepto</th>
                  <th className="px-3 py-2">Familia</th>
                  <th className="px-3 py-2">Subfamilia</th>
                  <th className="px-3 py-2">Cta. Gasto</th>
                  <th className="px-3 py-2">Alegra</th>
                  <th className="px-3 py-2 text-center w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {materiales.map((m, i) => {
                  const mv = movsByCodigo[m.codigo] || null;
                  const puedeEditar = !!mv?.puede_editar_nombre;
                  const puedeEliminar = !!mv?.puede_eliminar;
                  const totalMov = mv?.movimientos?.total ?? null;
                  const tooltipBloq = totalMov !== null && totalMov > 0
                    ? `Bloqueado: ${totalMov} movimiento(s) (OC=${mv.movimientos.ocs}, Recep=${mv.movimientos.recepciones}, Stock=${mv.movimientos.stock}, Desp=${mv.movimientos.despachos}, Req=${mv.movimientos.requisiciones})`
                    : 'Verificando...';
                  return (
                    <tr key={i} className="border-t hover:bg-gray-50" data-testid={`row-material-${m.codigo}`}>
                      <td className="px-3 py-1.5 font-medium">{m.descripcion} {m.descripcion2 && <span className="text-gray-400">{m.descripcion2}</span>}</td>
                      <td className="px-3 py-1.5 text-gray-500">{m.medida || '-'}</td>
                      <td className="px-3 py-1.5 text-center text-gray-500">{m.unidad}</td>
                      <td className="px-3 py-1.5 text-[10px] text-blue-700">{m.concepto || '-'}</td>
                      <td className="px-3 py-1.5 text-[10px] text-violet-700">{m.familia_nombre || '-'}</td>
                      <td className="px-3 py-1.5 text-[10px] text-green-700">{m.subfamilia_nombre || '-'}</td>
                      <td className="px-3 py-1.5 font-mono text-[9px]">{m.cuenta_gasto || '-'}</td>
                      <td className="px-3 py-1.5 font-mono text-[9px]">{m.cta_alegra || '-'}</td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => puedeEditar && openEditMaterial(m)}
                            disabled={!puedeEditar}
                            title={puedeEditar ? 'Modificar nombre' : tooltipBloq}
                            className={`p-1 rounded ${puedeEditar ? 'text-violet-600 hover:bg-violet-50' : 'text-gray-300 cursor-not-allowed'}`}
                            data-testid={`btn-editar-material-${m.codigo}`}
                          >
                            {puedeEditar ? <Pencil className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                          </button>
                          {isAdminGer && (
                            <button
                              onClick={() => puedeEliminar && openDeleteMaterial(m)}
                              disabled={!puedeEliminar}
                              title={puedeEliminar ? 'Eliminar material' : tooltipBloq}
                              className={`p-1 rounded ${puedeEliminar ? 'text-red-600 hover:bg-red-50' : 'text-gray-300 cursor-not-allowed'}`}
                              data-testid={`btn-eliminar-material-${m.codigo}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create Modal */}
      <Dialog open={createModal} onOpenChange={(o) => !saving && setCreateModal(o)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle><Plus className="w-5 h-5 inline mr-2 text-violet-600" />Nuevo Material</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1">Descripción *</label>
              <Input value={createForm.descripcion} onChange={(e) => setCreateForm(prev => ({ ...prev, descripcion: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Descripción 2</label>
                <Input value={createForm.descripcion2} onChange={(e) => setCreateForm(prev => ({ ...prev, descripcion2: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Medida</label>
                <Input value={createForm.medida} onChange={(e) => setCreateForm(prev => ({ ...prev, medida: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Unidad</label>
                <Input value={createForm.unidad} onChange={(e) => setCreateForm(prev => ({ ...prev, unidad: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Marca</label>
                <Input value={createForm.marca} onChange={(e) => setCreateForm(prev => ({ ...prev, marca: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Subfamilia *</label>
              <SearchableCombobox
                items={subfamilias.map((sf) => ({
                  value: sf.codpf,
                  label: sf.subfamilia,
                  subLabel: `${sf.concepto} / ${sf.familia}`,
                }))}
                value={createForm.codpf}
                onChange={(item) => setCreateForm(prev => ({ ...prev, codpf: item ? String(item.value) : '' }))}
                placeholder="Buscar subfamilia (auto-asigna familia y cuenta)…"
                emptyMessage="Sin subfamilias"
                testid="combobox-subfamilia-nuevo-material"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModal(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Crear Material
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Iter 414 — Enviar listado del Maestro por correo (Admin/Gerencia) */}
      <Dialog open={emailModal} onOpenChange={(o) => !emailSending && setEmailModal(o)}>
        <DialogContent className="max-w-lg" data-testid="modal-enviar-listado-correo">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Mail className="w-5 h-5" /> Enviar listado por correo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">
              Se enviará el Excel de <strong>artículos vigentes</strong> (con la hoja <strong>"Sin clasificar"</strong>)
              a los destinatarios indicados.
            </p>
            <div>
              <label className="block text-xs font-medium mb-1">Destinatarios</label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="email-recipients">
                {emailTo.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 rounded-full px-2.5 py-0.5 text-xs" data-testid={`email-chip-${e}`}>
                    {e}
                    <button type="button" onClick={() => removeEmailRecipient(e)} disabled={emailSending}
                      className="hover:text-red-600" title="Quitar" data-testid={`email-remove-${e}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {emailTo.length === 0 && <span className="text-xs text-muted-foreground italic">Sin destinatarios</span>}
              </div>
              <div className="flex gap-2">
                <Input
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmailRecipient(); } }}
                  placeholder="agregar correo y presiona Enter…"
                  className="flex-1"
                  data-testid="email-input"
                />
                <Button type="button" variant="outline" onClick={addEmailRecipient} disabled={emailSending} data-testid="email-add-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModal(false)} disabled={emailSending}>Cancelar</Button>
            <Button onClick={enviarListadoCorreo} disabled={emailSending || emailTo.length === 0}
              className="bg-blue-600 hover:bg-blue-700" data-testid="email-send-btn">
              {emailSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Iter 236.x — Editar nombre de material (solo sin movimientos) */}
      <Dialog open={editModal.open} onOpenChange={(o) => !editSaving && setEditModal({ open: o, material: o ? editModal.material : null })}>
        <DialogContent className="max-w-lg" data-testid="modal-editar-material">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-700">
              <Pencil className="w-5 h-5" /> Modificar Material
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900">
              Código <strong className="font-mono">{editModal.material?.codigo}</strong> · Sin movimientos registrados — puedes renombrar.
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Descripción *</label>
              <Input
                value={editForm.descripcion}
                onChange={(e) => setEditForm(prev => ({ ...prev, descripcion: e.target.value }))}
                data-testid="input-editar-descripcion"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Descripción 2</label>
                <Input
                  value={editForm.descripcion2}
                  onChange={(e) => setEditForm(prev => ({ ...prev, descripcion2: e.target.value }))}
                  data-testid="input-editar-descripcion2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Medida</label>
                <Input
                  value={editForm.medida}
                  onChange={(e) => setEditForm(prev => ({ ...prev, medida: e.target.value }))}
                  data-testid="input-editar-medida"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, material: null })} disabled={editSaving} data-testid="btn-cancelar-editar-material">
              Cancelar
            </Button>
            <Button
              onClick={saveEditMaterial}
              disabled={editSaving || !editForm.descripcion.trim()}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="btn-guardar-editar-material"
            >
              {editSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Pencil className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 236.x — Eliminar material (solo sin movimientos) */}
      <Dialog open={deleteModal.open} onOpenChange={(o) => !deleteSaving && setDeleteModal({ open: o, material: o ? deleteModal.material : null, movs: o ? deleteModal.movs : null })}>
        <DialogContent className="max-w-md" data-testid="modal-eliminar-material">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" /> Eliminar Material
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              ¿Confirmas eliminar el material <strong className="font-mono">{deleteModal.material?.codigo}</strong>?
            </p>
            <div className="text-xs bg-gray-50 border rounded p-2">
              <strong>{deleteModal.material?.descripcion}</strong>
              {deleteModal.material?.descripcion2 && (
                <div className="text-gray-500">{deleteModal.material.descripcion2}</div>
              )}
            </div>
            {deleteModal.movs?.puede_eliminar ? (
              <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900">
                Sin movimientos registrados — se puede eliminar de forma segura.
              </div>
            ) : (
              <div className="text-xs bg-amber-50 border border-amber-300 rounded p-2 text-amber-900 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{deleteModal.movs?.motivo || 'Bloqueado por movimientos.'}</span>
              </div>
            )}
            <div className="text-[10px] text-red-600">Esta acción no se puede deshacer.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, material: null, movs: null })} disabled={deleteSaving} data-testid="btn-cancelar-eliminar-material">
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteMaterial}
              disabled={deleteSaving || !deleteModal.movs?.puede_eliminar}
              data-testid="btn-confirmar-eliminar-material"
            >
              {deleteSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
